import { Form, Tooltip } from 'antd';
import { InfoIcon } from 'components/Icons';
import { useFeatureFlag } from "contexts/FeatureFlagContext";
import React, { forwardRef, useCallback, useMemo, useState } from "react";
import ToolsIntegrationsAPI from "restAPI/ToolsIntegrationAPI";
import { FieldTypes, urlRegexValidator } from "utils/formUtils";
import GenericPullCredentialsForm from "../GenericPullCredentialsForm";
import MicrosoftDefenderForEndPointMappingFormV2 from "./MicrosoftDefenderForEndPointMappingFormV2";
import MicrosoftDefenderForEndPointMappingForm from "./MicrosoftDefenderForEndpointMappingForm";

function MicrosoftDefenderForEndpointCredentialsForm(props, ref) {
  const featureFlags = useFeatureFlag();
  const [form] = Form.useForm();
  const [clientSecret, setClientSecret] = useState();
  const [fetchAssets, setFetchAssets] = useState(props.config?.fetchAssets ?? "false");

  const shouldShowAssetTags = useMemo(() => {
    return (
      featureFlags.RBVM_ASSETS &&
      featureFlags.MICROSOFT_DEFENDER_FOR_ENDPOINT_ADVANCED_HUNTING &&
      !! clientSecret &&
      fetchAssets === "true"
    );
  }, [featureFlags.RBVM_ASSETS, featureFlags.MICROSOFT_DEFENDER_FOR_ENDPOINT_ADVANCED_HUNTING, clientSecret, fetchAssets]);
  

  const fetchProjects = useCallback(
    (params) => {

      const {apiEndpoint, clientId, clientSecret, tenantId} = form.getFieldValue();

      if (apiEndpoint && clientId && clientSecret && tenantId) {

            const config = {
                apiEndpoint,
                clientId,
                clientSecret,
                tenantId
            };
          let { promise, cancel } =
            ToolsIntegrationsAPI.getProjectByToolName({
              config,
              ...params,
          }, "Microsoft Defender for Endpoint");
        
        promise = promise.then(response => {
          const {projects} =  response.data;
          return projects;
        });
        return { promise, cancel };
      }
    },[form],
  );

  const fields = useMemo(()=>[
    {
      name: "apiEndpoint",
      label: "API Endpoint",
      required: true,
      rules: [urlRegexValidator],
    },
    {
      name: "tenantId",
      label: "Tenant Id",
      required: true,
    },
    {
      name: "clientId",
      label: "Client Id",
      required: true,
      type: FieldTypes.PASSWORD,
    },
    {
      name: "clientSecret",
      label: "Client Secret",
      required: true,
      type: FieldTypes.PASSWORD,
      props: {
        onChange: (e) => setClientSecret(e.target.value) 
      }
    },
    ...(featureFlags.RBVM_ASSETS ? [{
      name: "fetchAssets",
      label: <>Fetch Assets <Tooltip title="Please ensure (User.Read.All) application permissions is associated with the client secret used for this integration."><InfoIcon className={'m-l-xs'} /></Tooltip></>,
      type: FieldTypes.BOOLEAN,
      disabled: !clientSecret,
      inline: true,
      props: {
        checked: fetchAssets,
        onChange: (e) => setFetchAssets((e?.target?.checked ? "true" : "false")),
      }
    }] : []),
    ...(shouldShowAssetTags ? [{
      name: "assetTagNames",
      label: "Select Asset Tags",
      required: false,
      type: FieldTypes.SELECT_WITH_PAGINATED_DATA,
      placeholder: "Leave Blank to select all",
      props: {
        mode: "multiple",
        api: fetchProjects,
      },
    }] : [])
  ], [clientSecret, featureFlags.RBVM_ASSETS, fetchAssets, fetchProjects, shouldShowAssetTags]);

  const parseExtraFields = useCallback((config) => {
    return {
      ...config,
      fetchAssets: config.fetchAssets === "true",
      assetTagNames: Array.isArray(config.assetTagNames)
        ? config.assetTagNames
        : (config.assetTagNames || "")
            .split(",")
            .map(id => id.trim())
            .filter(id => id.length > 0)
    };
  }, []);

  const beforeSave = useCallback((data) => {
    const transformedData = { ...data };
    if (Array.isArray(transformedData.assetTagNames)) {
      transformedData.assetTagNames = transformedData.assetTagNames.join(",");
    }
    return transformedData;
  }, []);


  return (
    <GenericPullCredentialsForm
      {...props}
      ref={ref}
      fields={fields}
      form={form}
      beforeSave = {beforeSave}
      parseExtraFields={parseExtraFields}
      MappingForm={featureFlags.MICROSOFT_DEFENDER_FOR_ENDPOINT_ADVANCED_HUNTING ? MicrosoftDefenderForEndPointMappingFormV2: MicrosoftDefenderForEndPointMappingForm}
      initialValue={{ apiEndpoint: "https://api.securitycenter.microsoft.com" }}
    />
  );
}

export default forwardRef(MicrosoftDefenderForEndpointCredentialsForm);